import {useState} from 'react'
import {number as formatNumber} from '@github-ui/formatters'
import {Button, CounterLabel} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {SyncIcon} from '@primer/octicons-react'
import {BaseSharedDismissalForm, validateComment, validateReasonSelected} from './BaseSharedDismissalForm'

export interface BaseSharedDismissalDialogProps {
  count: number
  title: string
  subtitle: string
  submitButtonLabel: string
  error: Error | null
  isPending?: boolean
  onSubmit: (arg0: {resolution: string; dismissalComment: string}) => void
  setOpen: (open: boolean) => void
  delegatedAlertDismissalEnabled: boolean
  dismissalOptions: Array<{value: string; label: string; description: string}>
}

export function BaseSharedDismissalDialog({
  setOpen,
  count,
  title,
  subtitle,
  submitButtonLabel,
  dismissalOptions,
  error,
  isPending,
  delegatedAlertDismissalEnabled,
  onSubmit,
}: BaseSharedDismissalDialogProps) {
  const commentRequired = delegatedAlertDismissalEnabled

  const [selectedCloseReason, setSelectedCloseReason] = useState('')
  const [dismissalComment, setDismissalComment] = useState('')

  const [reasonValidationError, setReasonValidationError] = useState<string | null>(null)
  const [commentValidationError, setCommentValidationError] = useState<string | null>(null)

  const validComment = validateComment({dismissalComment, commentRequired})
  const validReasonSelection = validateReasonSelected({reason: selectedCloseReason})

  const handleReasonChange = (value: string) => {
    setSelectedCloseReason(value)

    if (validateReasonSelected({reason: value})) {
      setReasonValidationError(null)
    }
  }

  const handleDismissalCommentChange = (value: string) => {
    setDismissalComment(value)

    if (validateComment({dismissalComment: value, commentRequired})) {
      setCommentValidationError(null)
    }
  }

  const onSubmitDismissal = async () => {
    if (!validComment) {
      setCommentValidationError('Comment cannot be empty')
    }

    if (!validReasonSelection) {
      setReasonValidationError('Dismissal reason is required')
    }

    if (!validComment || !validReasonSelection) {
      return
    }

    setCommentValidationError(null)
    setReasonValidationError(null)

    onSubmit({
      resolution: selectedCloseReason,
      dismissalComment,
    })
  }

  return (
    <Dialog
      width="large"
      height="auto"
      onClose={() => setOpen(false)}
      title={title}
      subtitle={subtitle}
      renderFooter={() => (
        <Dialog.Footer>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="primary"
            onClick={onSubmitDismissal}
            disabled={isPending}
            leadingVisual={isPending ? SyncIcon : null}
          >
            <span>{submitButtonLabel}</span>
            {count > 1 && (
              <CounterLabel variant="primary" className="ml-2">
                {formatNumber(count)}
              </CounterLabel>
            )}
          </Button>
        </Dialog.Footer>
      )}
    >
      <BaseSharedDismissalForm
        error={error}
        disabled={isPending}
        commentRequired={commentRequired}
        dismissalOptions={dismissalOptions}
        reasonSelected={selectedCloseReason}
        onReasonChange={handleReasonChange}
        reasonValidationError={reasonValidationError}
        dismissalComment={dismissalComment}
        onDismissalCommentChange={handleDismissalCommentChange}
        commentValidationError={commentValidationError}
        radioGroupName="resolution"
      />
    </Dialog>
  )
}
